import { AxiosRequestConfig } from 'axios'
import {
  ChangeAccessCode,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  InvitationInfo,
  LoginRequest,
  LoginResponse,
  RegisterInvitedTraineeRequest,
  RegisterRequest,
  RegisterTeamMemberRequest,
  ResetPasswordRequest,
  Response,
  TraineeInvitationInfo,
  User,
} from 'types'
import axiosInstance from './axios'

/* 200 if user find and 401 if user not find */
export const login = async (
  email: string,
  password: string,
  config?: AxiosRequestConfig,
): Promise<Response<LoginResponse>> => {
  const data = {
    email,
    password,
  }
  return axiosInstance.post<LoginRequest, Response<LoginResponse>>('/auth/login', data, config)
}

/* 200 if user find and 401 if user not find */
export const register = async (
  request: RegisterRequest,
  config?: AxiosRequestConfig,
): Promise<Response<User>> => {
  return axiosInstance.post<RegisterRequest, Response<User>>('/auth/register', request, config)
}

export const authenticate = async (): Promise<Response<LoginResponse>> => {
  return axiosInstance.post('/auth/authenticate')
}

export const getInvitationInfo = async (token: string): Promise<Response<InvitationInfo>> => {
  return axiosInstance.post('/auth/invitation-info', { token })
}

export const registerTeamMember = async (
  request: RegisterTeamMemberRequest,
): Promise<Response<User>> => {
  return axiosInstance.post('/auth/register-invited', request)
}

export const getTraineeInvitationInfo = async (
  token: string,
): Promise<Response<TraineeInvitationInfo>> => {
  return axiosInstance.post('/auth/trainee-invitation-info', { token })
}

export const registerInvitedTrainee = async (
  request: RegisterInvitedTraineeRequest,
): Promise<Response<User>> => {
  return axiosInstance.post('/auth/register-invited-trainee', request)
}

export const logoutRequest = () => axiosInstance.post('/auth/logout')

export const forgotPassword = async (request: ForgotPasswordRequest) => {
  return axiosInstance.post('/auth/forgot-password', request)
}

export const resetPassword = async (request: ResetPasswordRequest) => {
  return axiosInstance.post('/auth/reset-password', request)
}

export const changePassword = async (request: ChangePasswordRequest) => {
  return axiosInstance.post('/auth/change-password', request)
}

export const changeAccessCode = async (request: ChangeAccessCode) => {
  return axiosInstance.post('/auth/change-access-code', request)
}

export interface FinalizeSubscriptionPaymentResponse {
  status: string
  source: 'group' | 'individual'
}

/** Anonymous sync after 3DS during signup flow. */
export const finalizeSubscriptionPaymentAnonymous = (
  email: string,
  stripeSubscriptionId: string,
): Promise<{ data: FinalizeSubscriptionPaymentResponse }> => {
  return axiosInstance.post('/auth/finalize-subscription-payment', {
    email,
    stripeSubscriptionId,
  })
}

export interface RegisterInvitedCaregiverRequest {
  /** Care-team invitation JWT (may carry familyInvitationToken inside payload). */
  invitationToken: string
  firstname: string
  lastname: string
  password: string
  /** Required for phone-only invites: the JWT carries no email, so the backend takes it from the body. */
  email?: string
  /** Optional Channel Partner attribution code (PRT-XXXX-XXXX); not an access/promo code. */
  partnerCode?: string
}

/**
 * Registers a new caregiver from a care-team invitation JWT without payment.
 * When the JWT carries a familyInvitationToken, the user is also joined to the Family plan.
 */
export const registerInvitedCaregiver = (request: RegisterInvitedCaregiverRequest) => {
  return axiosInstance.post<RegisterInvitedCaregiverRequest, Response<User>>(
    '/auth/register-invited-caregiver',
    request,
  )
}

/**
 * Deletes an account that was created during registration but never got access — the card was
 * declined and no valid access code was entered. Anonymous: the user is not signed in at this
 * point, so the backend re-checks the credentials they just registered with.
 */
export const cancelRegistration = (email: string, password: string): Promise<void> => {
  return axiosInstance.post('/auth/cancel-registration', { email, password })
}
